// @flow
import get from 'lodash/get';
import has from 'lodash/has';
import every from 'lodash/every';
import noop from 'lodash/noop';
import toPairs from 'lodash/toPairs';
import fromPairs from 'lodash/fromPairs';
import map from 'lodash/map';
import union from 'lodash/union';
import { map as mapAsync } from 'p-iteration';
import { STATUS_ERROR } from 'box-ui-elements/es/constants';
import resin from '../../../common/utils/resin';
import {
  ACTION_ADD_ITEMS,
  ACTION_ITEMS_FILTERED_FROM_ADD,
  ACTION_UPLOAD_IN_PROGRESS,
  ACTION_SUBMIT_IN_PROGRESS,
  ACTION_SUBMIT,
  ACTION_SUBMISSION_FAILED,
  ACTION_ON_ITEM_REMOVE,
  ACTION_TOGGLE_UPLOAD_MANAGER,
  ACTION_UPLOAD_PROGRESS_UPDATE,
  ACTION_ON_ALL_FILES_UPLOADED,
  ACTION_VALIDATION_FAILED,
  ACTION_CLOSE_NOTIFICATION,
  ACTION_SELECT_FIELD_ON_CHANGE,
  ACTION_TEXT_FIELD_ON_CHANGE,
  ACTION_DATE_FIELD_ON_CHANGE,
  ACTION_TOO_MANY_FILES,
  ACTION_FILE_SIZE_LIMIT_EXCEEDED,
  ACTION_INVALID_FILE_NAME,
  ACTION_PROCESSING_IN_PROGRESS,
  ACTION_LIST_FIELD_ON_CHANGE,
} from './form-submitter-constant';
import selectors from './form-submitter-selector';
import FormResponseAPI from '../../../api/FormResponse';
import type { State, Action } from './form-submitter-type';
import { ItemQueue } from '../ItemQueue';
import { Uploader } from '../Uploader';
import {
  filterFilesNotAlreadyAdded,
  validateFormResponse,
  validateSelectField,
  validateTextField,
  validateDateField,
  validateUploadField,
  validateListField,
} from '../FormValidator';
import updateResponse from '../FormResponseAdapter';
import {
  ELEMENT_DESCRIPTION_ID,
  MAX_FILES_PER_SUBMISSION,
  FILES,
  MAX_FILE_SIZE,
  FORM_RESPONSE_STATUS,
  REGEX_FILE_NAME_VALIDATION,
} from '../../../constants';
import BoxFileQueue from '../../../common/utils/BoxFileQueue';

const defaultCallbacks = {
  onSuccessfulSubmission: noop,
  onInvalidFormError: noop,
};

type CallbacksType = {
  onInvalidFormError?: () => {},
  onSuccessfulSubmission?: number => {},
};

const resinEvent = {
  action: 'programmatic',
  page: 'file-request-submission',
  feature: 'filerequest',
  component: 'submissionpage',
};

export default (callbacks: CallbacksType) => {
  const { onSuccessfulSubmission, onInvalidFormError } = {
    ...defaultCallbacks,
    ...callbacks,
  };
  return {
    [ACTION_ADD_ITEMS]: (dispatch, getState: () => State) => async (
      action: Action,
    ) => {
      const { id, items } = action;
      const state: State = getState();

      if (
        items.length >
        MAX_FILES_PER_SUBMISSION - selectors.countAllItemsInItemQueues(state)
      ) {
        dispatch({ type: ACTION_TOO_MANY_FILES });
        return;
      }

      const itemsArray = Array.from(items);

      const itemsToAdd = await filterFilesNotAlreadyAdded(
        itemsArray,
        selectors.getAllItemsInItemQueues(state),
      );

      const hasFilesLargerThanMaxSize = Boolean(
        itemsToAdd.filter(file => file.size > MAX_FILE_SIZE).length,
      );

      if (hasFilesLargerThanMaxSize) {
        dispatch({ type: ACTION_FILE_SIZE_LIMIT_EXCEEDED });
        return;
      }

      const hasFilesInvalidName = itemsToAdd.find(file =>
        REGEX_FILE_NAME_VALIDATION.test(String(file.name)),
      );

      if (hasFilesInvalidName !== undefined) {
        dispatch({ type: ACTION_INVALID_FILE_NAME });
        return;
      }

      if (itemsToAdd.length !== itemsArray.length) {
        dispatch({
          type: ACTION_ITEMS_FILTERED_FROM_ADD,
        });
        if (itemsToAdd.length === 0) return;
      }

      const itemQueue = get(
        state,
        `itemQueues.${id}`,
        new ItemQueue(id, state.folderId),
      );
      itemQueue.addFilesToUploadQueue(itemsToAdd);

      const { validateOnChange } = state;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateUploadField(required, itemQueue.items);
        dispatch({ ...action, items: itemsToAdd, itemQueue, errorId });
      } else {
        dispatch({
          ...action,
          items: itemsToAdd,
          itemQueue,
          errorId: get(state, `errors.${id}`),
        });
      }
    },
    [ACTION_ON_ITEM_REMOVE]: (dispatch, getState: () => State) => (
      action: Action,
    ) => {
      const state = getState();
      const { id, item } = action;
      const itemQueue = get(
        state,
        `itemQueues.${id}`,
        new ItemQueue(id, state.folderId),
      );
      itemQueue.removeItem(item);
      const { validateOnChange } = state;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateUploadField(required, itemQueue.items);
        dispatch({ ...action, itemQueue, errorId });
      } else {
        dispatch({ ...action, itemQueue, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_SELECT_FIELD_ON_CHANGE]: (dispatch, getState: () => State) => (
      action: Action,
    ) => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValues } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateSelectField(required, newValues);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_LIST_FIELD_ON_CHANGE]: (dispatch, getState: () => State) => (
      action: Action,
    ) => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValues } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateListField(required, newValues);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_TEXT_FIELD_ON_CHANGE]: (dispatch, getState: () => State) => (
      action: Action,
    ) => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValue } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { textType, required } = component;
        const errorId = validateTextField(textType, required, newValue);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_DATE_FIELD_ON_CHANGE]: (dispatch, getState: () => State) => (
      action: Action,
    ) => {
      const state = getState();
      const { validateOnChange } = state;
      const { id, newValue } = action;
      if (validateOnChange) {
        const component = get(state, `components.${id}`);
        const { required } = component;
        const errorId = validateDateField(required, newValue);
        dispatch({ ...action, errorId });
      } else {
        dispatch({ ...action, errorId: get(state, `errors.${id}`) });
      }
    },
    [ACTION_TOGGLE_UPLOAD_MANAGER]: dispatch => (action: Action) => {
      dispatch(action);
    },
    [ACTION_CLOSE_NOTIFICATION]: dispatch => (action: Action) => {
      dispatch(action);
    },
    [ACTION_SUBMIT]: (dispatch, getState: () => State) => async () => {
      const state = getState();

      // Validate the form
      const errors = validateFormResponse(
        state.components,
        state.itemQueues,
        state.response,
      );

      // If any errors exist, do NOT submit the form.
      if (Object.keys(errors).length > 0) {
        dispatch({ type: ACTION_VALIDATION_FAILED, errors });
        setTimeout(onInvalidFormError);
        return;
      }

      resin.recordAction({
        ...resinEvent,
        target: 'submit',
        data: {
          file_request_url_id: state.frUrlId,
          num_files_uploaded: selectors.countAllItemsInItemQueues(state),
        },
      });

      // Set UI state to submit in progress
      dispatch({ type: ACTION_SUBMIT_IN_PROGRESS });

      const uploader = new Uploader(state.folderId, state.itemQueues);

      let itemValues;
      try {
        // Get all upload items a format that form response likes
        itemValues = fromPairs(
          await mapAsync(
            toPairs(state.itemQueues),
            async ([elementId, itemQueue]) => {
              const items = await mapAsync(itemQueue.items, async item => ({
                local_path: item.file.name,
                size: get(item, 'size', null),
              }));
              return [elementId, { type: FILES, data: items }];
            },
          ),
        );
      } catch (e) {
        dispatch({ type: ACTION_SUBMISSION_FAILED });
        // eslint-disable-next-line
        console.error('itemValues creation error: ', e);
        resin.recordAction({
          ...resinEvent,
          target: 'submit|sha_error',
          data: {
            file_request_url_id: state.frUrlId,
            error_code: get(e, 'code'),
            error_message: get(e, 'message'),
          },
        });
        return;
      }

      const items = union(
        ...map(state.itemQueues, itemQueue => itemQueue.items),
      );

      // Patch form response with status COMPLETE
      // If patch successful, go to form submit completed page
      const onComplete = ({ formResponseKey }) => {
        dispatch({ type: ACTION_ON_ALL_FILES_UPLOADED });
        // console.log('form submission completed...');
        resin.recordAction({
          ...resinEvent,
          target: 'submit|completed',
          data: {
            file_request_url_id: state.frUrlId,
            form_response_key: formResponseKey,
          },
        });
        onSuccessfulSubmission(items.length);
      };

      const allPatchOfFileIdCompleted = ({ formResponse }) =>
        every(formResponse, element => {
          if (get(element, 'type') !== FILES) return true;
          return every(get(element, 'data'), fileJSON =>
            has(fileJSON, 'file_id'),
          );
        });

      // This is queue is used to hold patch file id calls so that we can sychronize them in order
      // We need to do this until we can have locks on form response table update

      const boxFileQueue = new BoxFileQueue();
      // Options to be passed to uploader
      // isResumableUploadsEnabled
      // onUploadProgressUpdate - called as upload in progress (percentage update) and when upload is complete
      const options = {
        isResumableUploadsEnabled: state.isResumableUploadsEnabled,
        onUploadProgressUpdate: async (item, { formResponseKey, destroy }) => {
          if (item.status === STATUS_ERROR) {
            // if 401 thrown, try fetching new token
            if (get(item, 'error.status') === 401) {
              uploader.onRetryErrorItem(item);
            }
          }
          // If boxFile is available in item, that mean the item has been uploaded succesfully to box.
          if (item.boxFile) {
            const patchCall = payload =>
              FormResponseAPI.patchFileIds(
                formResponseKey,
                state.form.versionId,
                payload,
                state.isAuthenticationRequired,
              )
                .then(formResponse => {
                  // after each patch, check if all items has been patched with file Id on form response, then call onComplete to patch form response to COMPLETED status.
                  if (allPatchOfFileIdCompleted(formResponse)) {
                    onComplete({ formResponseKey, destroy });
                  }
                })
                .catch(e => {
                  // eslint-disable-next-line
                console.error('patchFileIdToFormResponse: ', e);
                  resin.recordAction({
                    ...resinEvent,
                    target: 'submit|error',
                    data: {
                      file_request_url_id: state.frUrlId,
                      form_response_key: formResponseKey,
                    },
                  });
                  dispatch({ type: ACTION_SUBMISSION_FAILED });
                  destroy();
                });
            boxFileQueue.setPatchCall(patchCall);
            boxFileQueue.enqueue(item);
          }
          if (uploader.allUploadCompleted()) {
            dispatch({ type: ACTION_PROCESSING_IN_PROGRESS });
          }

          dispatch({ type: ACTION_UPLOAD_PROGRESS_UPDATE });
        },
        conflictCallback: fileName => {
          const extension = fileName.substr(fileName.lastIndexOf('.')) || '';
          const name = fileName.substr(0, fileName.lastIndexOf('.'));
          const now = new Date().toISOString();
          const nowFormatted = `${now.substr(0, now.lastIndexOf('.'))}`
            .replace(/:/g, '.')
            .replace('T', ' ');
          return `${name} ${nowFormatted}${extension}`;
        },
      };

      // Initial submit of the form response
      const payload = {
        ...updateResponse(state.components, state.response),
        ...itemValues,
        folder: {
          type: 'folder',
          id: get(state, 'folderId', null),
        },
      };

      const description = get(payload, ELEMENT_DESCRIPTION_ID);

      const formResponse = await FormResponseAPI.createFormResponse(
        state.frUrlId,
        state.form.versionId,
        payload,
        state.isAuthenticationRequired,
      ).catch(e => {
        // eslint-disable-next-line
        console.error('createFormResponse: ', e);
        resin.recordAction({
          ...resinEvent,
          target: 'submit|error',
          data: {
            file_request_url_id: state.frUrlId,
          },
        });
        dispatch({ type: ACTION_SUBMISSION_FAILED });
      });

      if (formResponse) {
        if (formResponse.status === FORM_RESPONSE_STATUS.COMPLETE) {
          onComplete({ formResponseKey: formResponse.key });
        } else {
          // set token and formReponse key received form initial submit of form response on uploader
          uploader.options = {
            ...uploader.options,
            ...options,
            token: formResponse.uploadToken,
            description,
          };
          uploader.fileRequestUrlId = state.frUrlId;
          uploader.formResponseKey = formResponse.key;
          uploader.fetchNewToken = () => {
            return FormResponseAPI.fetchToken(
              state.frUrlId,
              formResponse.key,
              state.isAuthenticationRequired,
            ).catch(e => {
              // eslint-disable-next-line
                console.error('fetchToken: ', e);
              resin.recordAction({
                ...resinEvent,
                target: 'submit|error',
                data: {
                  file_request_url_id: state.frUrlId,
                  form_response_key: formResponse.key,
                },
              });
              uploader.destroy();
              dispatch({ type: ACTION_SUBMISSION_FAILED });
              throw e;
            });
          };

          // Set UI state to upload in progress.
          dispatch({ type: ACTION_UPLOAD_IN_PROGRESS, uploader });

          // Begin upload.
          uploader.submit();
        }
      }
    },
  };
};
